import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface Study {
  study_id: string;
  year: number;
  [key: string]: any;
}

interface Intervention {
  study_id: string;
  mushroom_id: string;
  [key: string]: any;
}

interface Mushroom {
  mushroom_id: string;
  common_name: string;
  [key: string]: any;
}

interface ResearchTrendsChartProps {
  studies: Study[];
  interventions: Intervention[];
  mushrooms: Mushroom[];
}

export default function ResearchTrendsChart({ studies, interventions, mushrooms }: ResearchTrendsChartProps) {
  // Group studies by year
  const yearData: Record<number, { total: number; bySpecies: Record<string, number> }> = {};
  
  studies.forEach(study => {
    if (!yearData[study.year]) {
      yearData[study.year] = { total: 0, bySpecies: {} };
    }
    yearData[study.year].total++;
    
    // Find mushroom for this study
    const intervention = interventions.find(i => i.study_id === study.study_id);
    if (intervention) {
      const mushroom = mushrooms.find(m => m.mushroom_id === intervention.mushroom_id);
      if (mushroom) {
        const speciesName = mushroom.common_name;
        yearData[study.year].bySpecies[speciesName] = (yearData[study.year].bySpecies[speciesName] || 0) + 1;
      }
    }
  });

  // Find top 3 species by total studies
  const speciesCounts: Record<string, number> = {};
  Object.values(yearData).forEach(year => {
    Object.entries(year.bySpecies).forEach(([species, count]) => {
      speciesCounts[species] = (speciesCounts[species] || 0) + count;
    });
  });
  
  const top3Species = Object.entries(speciesCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([species]) => species);

  // Prepare chart data
  const chartData = Object.entries(yearData)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([year, data]) => {
      const point: any = {
        year: Number(year),
        total: data.total
      };
      
      top3Species.forEach(species => {
        point[species] = data.bySpecies[species] || 0;
      });
      
      return point;
    });

  const colors = ['#A98A2C', '#C26A1E', '#B45F3D'];

  return (
    <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(10,36,99,0.08)] rounded-xl">
      <CardHeader>
        <CardTitle>Clinical Trial Publications by Year</CardTitle>
        <CardDescription>Research trends from 1998 to 2025</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E1D8" />
            <XAxis 
              dataKey="year" 
              stroke="#6E6B64"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#6E6B64"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '2px solid #A98A2C',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
            />
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke="#2A2A28" 
              strokeWidth={3}
              name="Total Studies"
              dot={{ fill: '#2A2A28', r: 4 }}
            />
            {top3Species.map((species, index) => (
              <Line
                key={species}
                type="monotone"
                dataKey={species}
                stroke={colors[index]}
                strokeWidth={2}
                name={species}
                dot={{ fill: colors[index], r: 3 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
